import type { LevelMappingBucket } from '../../data-contract/position-level-mapping';
import type {
    ManagerChangeRequest,
    OrgChartRequest,
    PositionAccessArea,
    PositionCountsPathEntry,
    PositionCountsRequest,
    PositionFilters,
} from '../../data-contract/position-management';

export type PositionArchiveScope = NonNullable<PositionFilters['archived']>;

export type PositionListScope = 'global-search' | 'list' | 'picker';

export const WORK_QUEUE_COUNTS_SEGMENT = 'work-queue-counts';
export const TRANSFORMATION_SEGMENT = 'transformation';

export const positionManagementKeys = {
    accessCandidates: (query: string) => [...positionManagementKeys.all, 'access-candidates', query] as const,
    accessCoverage: (architectureId: string, area: PositionAccessArea) =>
        [...positionManagementKeys.all, 'access-coverage', architectureId, area] as const,
    accessGrants: () => [...positionManagementKeys.all, 'access-grants'] as const,
    accessGrantsOfUser: (userId: string) => [...positionManagementKeys.accessGrants(), 'user', userId] as const,
    all: ['position-management'] as const,
    anyPosition: () => [...positionManagementKeys.all, 'position'] as const,
    architecture: (architectureId: string) => [...positionManagementKeys.architectures(), architectureId] as const,
    architectures: () => [...positionManagementKeys.all, 'architecture'] as const,
    assignees: (query: string) => [...positionManagementKeys.all, 'assignees', query] as const,
    bootstrap: () => [...positionManagementKeys.all, 'bootstrap'] as const,
    companyInformation: (architectureId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'company-information'] as const,
    companyLookup: (architectureId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'company-lookup'] as const,
    counts: (architectureId: string, request: PositionCountsRequest) =>
        [...positionManagementKeys.positions(architectureId), 'counts', request] as const,
    latestManagerChange: (architectureId: string) =>
        [...positionManagementKeys.managerChange(architectureId), 'latest'] as const,
    levelMappingRun: (architectureId: string, runId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'level-mapping', runId] as const,
    levelMappingUnits: (architectureId: string, runId: string, bucket: LevelMappingBucket) =>
        [...positionManagementKeys.levelMappingRun(architectureId, runId), 'units', bucket] as const,
    events: (positionId: string) => [...positionManagementKeys.position(positionId), 'events'] as const,
    levelPicker: () => [...positionManagementKeys.all, 'level-picker'] as const,
    levelPickerChildren: (jobArchitectureId: string, parentId: string | null) =>
        [...positionManagementKeys.levelPicker(), jobArchitectureId, 'children', parentId] as const,
    levelPickerLevels: (jobArchitectureId: string, languageTag: string, query: string) =>
        [...positionManagementKeys.levelPicker(), jobArchitectureId, 'levels', languageTag, query] as const,
    levelPickerPath: (jobArchitectureId: string, levelId: string | undefined) =>
        [...positionManagementKeys.levelPicker(), jobArchitectureId, 'path', levelId] as const,
    levelPositionCounts: () => [...positionManagementKeys.all, 'level-position-counts'] as const,
    managerChange: (architectureId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'manager-change'] as const,
    managerChangeRetry: (architectureId: string) =>
        [...positionManagementKeys.managerChange(architectureId), 'retry'] as const,
    managerChangePreview: (architectureId: string, request: ManagerChangeRequest | undefined) =>
        [...positionManagementKeys.positions(architectureId), 'manager-change-preview', request] as const,
    managerChangeSkipped: (architectureId: string, runId: string) =>
        [...positionManagementKeys.managerChange(architectureId), 'skipped', runId] as const,
    nodeManagers: (architectureId: string, path: readonly PositionCountsPathEntry[]) =>
        [...positionManagementKeys.positions(architectureId), 'node-managers', path] as const,
    orgChart: (architectureId: string, request: OrgChartRequest) =>
        [...positionManagementKeys.positions(architectureId), 'org-chart', request] as const,
    organizationOptions: () => [...positionManagementKeys.all, 'organization-options'] as const,
    plan: (planId: string | undefined) => [...positionManagementKeys.all, 'plan', planId] as const,
    plans: (architectureId: string) => [...positionManagementKeys.architecture(architectureId), 'plans'] as const,
    position: (positionId: string) => [...positionManagementKeys.anyPosition(), positionId] as const,
    positionDetail: (positionId: string, version?: number) =>
        [...positionManagementKeys.positionDetails(positionId), version ?? 'default'] as const,
    positionDetails: (positionId: string) => [...positionManagementKeys.position(positionId), 'detail'] as const,
    positionList: (architectureId: string, filters: PositionFilters, scope: PositionListScope = 'list') =>
        [...positionManagementKeys.positions(architectureId), scope, filters] as const,
    positions: (architectureId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'positions'] as const,
    positionTotal: (architectureId: string, archived: PositionArchiveScope) =>
        [...positionManagementKeys.positions(architectureId), 'total', archived] as const,
    reviewers: (query: string) => [...positionManagementKeys.all, 'reviewers', query] as const,
    setupPackage: (runId: string | undefined) => [...positionManagementKeys.all, 'setup-package', runId] as const,
    setupRun: (architectureId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'setup-run'] as const,
    workQueueCounts: (architectureId: string) =>
        [...positionManagementKeys.positions(architectureId), WORK_QUEUE_COUNTS_SEGMENT] as const,
    workspace: (architectureId: string) =>
        [...positionManagementKeys.architecture(architectureId), 'workspace'] as const,
} as const;
